import React from 'react'
import { differenceInSeconds } from 'date-fns'
import { Menu, MenuItem, Tooltip } from '@mui/material'
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  EditOutlined as EditIcon,
  ExpandMore as ExpandIcon,
  Inventory2Outlined as PackageIcon,
  ChevronRightRounded,
} from '@mui/icons-material'
import { useDispatch, useSelector } from '../../store'
import { Subscriber } from '../../store/user/reducer'
import { fetchPackage } from '../../store/package/actionCreator'
import { showSnackbar } from '../../store/common/actionCreator'
import {
  patchUserAdditionalPackage,
  patchUserPackage,
  putAnnualBonus,
  putBalance,
  putQuota,
  putRenewal,
  setSubscriberAiAssistant,
  setSubscriberIrbisPro,
  setSubscriberIrbisProUpgrade,
} from '../../store/dashboard/actionCreator'
import { formatDate } from '../../function/date'
import s from './PackagesCardV2.module.css'
import l from '../../page/SubscribersPageV2.module.css'
import { useAdminTheme } from '../../hook/useAdminTheme'

type Props = {
  data: Subscriber
  // Lets the page keep its hero/KPI cards in sync after an edit here.
  onChange: (patch: Partial<Subscriber>) => void
}

// Same limits as the old BlockPackage / AdditionalPackage Joi schemas.
const LIMITS = {
  balance: { min: 0, max: 99999, integer: false, label: 'Balance' },
  bonus: { min: 0, max: 99999, integer: false, label: 'Annual bonus' },
  renewal: { min: 0, max: 365, integer: true, label: 'Renewal' },
  quota: { min: 0, max: 99999, integer: false, label: 'Quota' },
}

const QUOTA_KEYS = [
  'quotaProfilerLookups',
  'quotaProfilerCrawlers',
  'quotaProfilerCases',
  'quotaProfilerPeriodic',
  'quotaWebintCases',
  'quotaWebintCrawlers',
  'quotaWebintMonitoring',
  'quotaWebintPeriodic',
] as const
type QuotaKey = typeof QUOTA_KEYS[number]

const PROFILER: Array<[QuotaKey, string]> = [
  ['quotaProfilerLookups', 'Lookups'],
  ['quotaProfilerCrawlers', 'Crawlers'],
  ['quotaProfilerCases', 'Cases'],
]
const WEBINT: Array<[QuotaKey, string]> = [
  ['quotaWebintCases', 'Cases'],
  ['quotaWebintCrawlers', 'Crawlers'],
  ['quotaWebintMonitoring', 'Monitoring'],
]

function daysLeft(endDate: string | Date | null | undefined) {
  if (!endDate) return 0
  const days = Math.ceil(differenceInSeconds(new Date(endDate), new Date()) / 60 / 60 / 24)
  return Number.isFinite(days) && days > 0 ? days : 0
}

// Quotas arrive as strings or numbers, or are missing; the old block printed
// "undefined" for missing ones.
function quotaValue(raw: unknown): string {
  if (raw === null || raw === undefined || raw === '' || raw === 'undefined') return '0'
  return String(raw)
}

function Switch({ on, label, onToggle, busy }: { on: boolean; label: string; onToggle: () => void; busy?: boolean }) {
  return (
    <button type="button" className={`${s.switch} ${on ? s.switchOn : ''}`} onClick={onToggle} disabled={busy} aria-pressed={on}>
      {label}
      <span className={s.track} />
    </button>
  )
}

function Editor({
  initial,
  onSave,
  onCancel,
  busy,
  children,
}: {
  initial: string
  onSave: (value: string) => void
  onCancel: () => void
  busy: boolean
  children?: (setValue: (v: string) => void, value: string) => React.ReactNode
}) {
  const [value, setValue] = React.useState(initial)
  return (
    <>
      <div className={s.editor}>
        <input
          autoFocus
          value={value}
          inputMode="decimal"
          onFocus={(e) => e.target.select()}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSave(value)
            if (e.key === 'Escape') onCancel()
          }}
        />
        <button className={`${s.ib} ${s.ibOk}`} onClick={() => onSave(value)} disabled={busy} aria-label="Save">
          <CheckIcon sx={{ fontSize: 16 }} />
        </button>
        <button className={s.ib} onClick={onCancel} aria-label="Cancel">
          <CloseIcon sx={{ fontSize: 16 }} />
        </button>
      </div>
      {children?.(setValue, value)}
    </>
  )
}

export function PackagesCardV2({ data, onChange }: Props) {
  // the menus are portalled out of the page — they carry the theme themselves
  const { theme } = useAdminTheme()
  const menuLook = {
    PaperProps: { className: `${l.theme} ${s.menuPaper}`, 'data-theme': theme } as any,
    MenuListProps: { className: s.menuList },
  }
  const dispatch = useDispatch()
  const { packages } = useSelector((state) => state.package)

  const [editing, setEditing] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState<string | null>(null)
  const [mainMenu, setMainMenu] = React.useState<HTMLElement | null>(null)
  const [annualMenu, setAnnualMenu] = React.useState<{ anchor: HTMLElement; pkg: any } | null>(null)
  const [addonMenu, setAddonMenu] = React.useState<HTMLElement | null>(null)

  const [aiAssistant, setAiAssistant] = React.useState(!!data.package?.isAiAssistant)
  const [irbisPro, setIrbisPro] = React.useState(!!data.package?.isIrbisPro)
  const [irbisProUpgrade, setIrbisProUpgrade] = React.useState(!!data.irbisproUpgrade)
  const [quotas, setQuotas] = React.useState<Record<QuotaKey, string>>(() => {
    const q = (data.additionalPackageQouta || {}) as Record<string, unknown>
    return QUOTA_KEYS.reduce((acc, key) => ({ ...acc, [key]: quotaValue(q[key]) }), {} as Record<QuotaKey, string>)
  })

  React.useEffect(() => {
    dispatch(fetchPackage)
  }, [dispatch])

  const days = daysLeft(data.packageEndDate)
  const addonDays = daysLeft(data.additionalPackageEndDate)
  const creditsPerUnit = data.package?.creditsPerUnit
  const credits = creditsPerUnit ? creditsPerUnit * Number(data.packageBalance || 0) : null

  const validate = (raw: string, limit: typeof LIMITS[keyof typeof LIMITS]) => {
    const value = Number(String(raw).replace(',', '.'))
    if (raw.trim() === '' || Number.isNaN(value) || value < limit.min || value > limit.max || (limit.integer && !Number.isInteger(value))) {
      dispatch(showSnackbar('error', `${limit.label} must be ${limit.integer ? 'a whole number ' : ''}between ${limit.min} and ${limit.max}`))
      return null
    }
    return value
  }

  const run = (key: string, action: (done: () => void) => void) => {
    setBusy(key)
    action(() => {
      setBusy(null)
      setEditing(null)
    })
  }

  const saveBalance = (raw: string) => {
    const value = validate(raw, LIMITS.balance)
    if (value === null) return
    run('balance', (done) =>
      dispatch(putBalance(data.id, value, () => {
        onChange({ packageBalance: value })
        done()
      })),
    )
  }

  const saveBonus = (raw: string) => {
    const value = validate(raw, LIMITS.bonus)
    if (value === null) return
    run('bonus', (done) =>
      dispatch(putAnnualBonus(data.id, value, () => {
        onChange({ annualBonus: value })
        done()
      })),
    )
  }

  // The API takes a day delta, like the old block: new days − current days.
  const saveRenewal = (raw: string) => {
    const value = validate(raw, LIMITS.renewal)
    if (value === null) return
    const diff = value - days
    if (diff === 0) {
      setEditing(null)
      return
    }
    run('renewal', (done) =>
      dispatch(putRenewal(data.id, diff, () => {
        const base = data.packageEndDate ? new Date(data.packageEndDate) : new Date()
        onChange({ packageEndDate: new Date(base.getTime() + diff * 24 * 60 * 60 * 1000) })
        done()
      })),
    )
  }

  const saveQuota = (key: QuotaKey, raw: string) => {
    const value = validate(raw, LIMITS.quota)
    if (value === null) return
    const next = { ...quotas, [key]: String(value) }
    // The endpoint replaces all quotas at once; note its "quotaWebIntPeriodic"
    // spelling, kept from the old block.
    const payload = {
      quotaProfilerLookups: next.quotaProfilerLookups,
      quotaProfilerCrawlers: next.quotaProfilerCrawlers,
      quotaProfilerCases: next.quotaProfilerCases,
      quotaProfilerPeriodic: next.quotaProfilerPeriodic,
      quotaWebintMonitoring: next.quotaWebintMonitoring,
      quotaWebintCases: next.quotaWebintCases,
      quotaWebintCrawlers: next.quotaWebintCrawlers,
      quotaWebIntPeriodic: next.quotaWebintPeriodic,
    }
    run(key, (done) =>
      dispatch(putQuota(data.uid, payload, () => {
        setQuotas(next)
        done()
      })),
    )
  }

  const toggleAi = () => {
    setBusy('ai')
    dispatch(setSubscriberAiAssistant(data.id, !aiAssistant, (value: boolean) => {
      setAiAssistant(value)
      setBusy(null)
    }))
  }
  const toggleIrbisPro = () => {
    setBusy('irbisPro')
    dispatch(setSubscriberIrbisPro(data.id, !irbisPro, (value: boolean) => {
      setIrbisPro(value)
      setBusy(null)
    }))
  }
  const toggleUpgrade = () => {
    setBusy('upgrade')
    dispatch(setSubscriberIrbisProUpgrade(data.id, !irbisProUpgrade, (value: boolean) => {
      setIrbisProUpgrade(value)
      setBusy(null)
    }))
  }

  const selectMain = (pkg: any, isAnnual: boolean) => {
    setMainMenu(null)
    setAnnualMenu(null)
    dispatch(patchUserPackage(data.id, pkg.id, () => {
      onChange({ package: data.package ? { ...data.package, id: pkg.id, title: pkg.title, price: pkg.price } : data.package })
    }, isAnnual))
  }

  const selectAddon = (pkg: any) => {
    setAddonMenu(null)
    dispatch(patchUserAdditionalPackage(data.id, pkg.id, () => {
      onChange({ additionalPackage: { id: pkg.id, title: pkg.title, price: pkg.price } })
    }))
  }

  // Same package filtering as the old ContextMenu (main) and PopperPackage (add-on).
  const isTelegramUser = data.uid?.length === 36
  const mainOptions = (packages || []).filter((el: any) => {
    const isTelegramPackage = el.type === 'telegrambt'
    const isGeneral = el.type === 'general' || el.type === 'demo' || (el.type === 'personal' && el.uid === data.uid)
    const isNotCurrent = el.id !== data.package?.id
    // Packages without an explicit isAnnual flag were hidden by the old menu too.
    const hasPlan = el.isAnnual === true || el.isAnnual === false
    return hasPlan && ((isTelegramUser && isTelegramPackage) || (!isTelegramUser && isGeneral && isNotCurrent))
  })
  const addonOptions = (packages || []).filter(
    (el: any) => el.type === 'webinthub' || (el.type === 'webinthub_demo' && el.uid === data.uid),
  )

  const editBtn = (key: string, title: string) => (
    <Tooltip title={title}>
      <button className={s.edit} onClick={() => setEditing(key)} aria-label={title}>
        <EditIcon sx={{ fontSize: 15 }} />
      </button>
    </Tooltip>
  )

  return (
    <div className={s.body}>
      {/* Main package */}
      <div className={s.pkg}>
        <div className={s.top}>
          <span className={s.tier}><PackageIcon sx={{ fontSize: 16 }} /></span>
          <div>
            <div className={s.lbl}>Main package</div>
            <div className={s.name}>
              {data.package?.title || 'No package'}
              <button className={s.change} onClick={(e) => setMainMenu(e.currentTarget)}>
                Change <ExpandIcon sx={{ fontSize: 15 }} />
              </button>
            </div>
          </div>
          <span className={s.spacer} />
          <Switch on={aiAssistant} label="AI Assistant" onToggle={toggleAi} busy={busy === 'ai'} />
          <Switch on={irbisPro} label="IRBIS Pro" onToggle={toggleIrbisPro} busy={busy === 'irbisPro'} />
        </div>

        <div className={s.tiles}>
          <div className={s.tile}>
            <div className={s.lbl}>Balance</div>
            {editing === 'balance' ? (
              <Editor initial={String(data.packageBalance ?? 0)} onSave={saveBalance} onCancel={() => setEditing(null)} busy={busy === 'balance'} />
            ) : editing === 'bonus' ? (
              <>
                <div className={s.sub}>Annual bonus</div>
                <Editor initial={String(data.annualBonus ?? 0)} onSave={saveBonus} onCancel={() => setEditing(null)} busy={busy === 'bonus'} />
              </>
            ) : (
              <>
                <div className={s.value}>{data.packageBalance ?? 0} <small>€</small></div>
                <div className={s.sub}>
                  Annual bonus {data.annualBonus ?? 0} €
                  <button className={s.inlineEdit} onClick={() => setEditing('bonus')}>edit</button>
                </div>
                {editBtn('balance', 'Edit balance')}
              </>
            )}
          </div>

          <div className={s.tile}>
            <div className={s.lbl}>Credits</div>
            <div className={s.value}>{credits !== null ? credits.toLocaleString() : '—'}</div>
            <div className={s.sub}>{creditsPerUnit ? `${creditsPerUnit} credits per €` : 'No credit rate for this package'}</div>
          </div>

          <div className={s.tile}>
            <div className={s.lbl}>Renewal</div>
            {editing === 'renewal' ? (
              <Editor initial={String(days)} onSave={saveRenewal} onCancel={() => setEditing(null)} busy={busy === 'renewal'}>
                {(setValue, value) => (
                  <div className={s.stepper}>
                    {[-30, -7, 7, 30, 365].map((step) => (
                      <button
                        key={step}
                        onClick={() => setValue(String(Math.min(365, Math.max(0, (Number(value) || 0) + step))))}
                      >
                        {step > 0 ? `+${step}` : `−${-step}`}
                      </button>
                    ))}
                  </div>
                )}
              </Editor>
            ) : (
              <>
                <div className={s.value}>{days} <small>days</small></div>
                <div className={s.sub}>
                  {data.packageEndDate ? formatDate(data.packageEndDate, 'dd/MM/yyyy') : '—'}
                  <span className={`${s.chip} ${data.isAutoRenewal ? s.chipOk : s.chipBad}`}>
                    <i />Auto-renew {data.isAutoRenewal ? 'on' : 'off'}
                  </span>
                </div>
                {editBtn('renewal', 'Edit renewal')}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Additional package */}
      {data.additionalPackage ? (
        <div className={s.pkg}>
          <div className={s.top}>
            <span className={`${s.tier} ${s.tierAddon}`}><AddIcon sx={{ fontSize: 16 }} /></span>
            <div>
              <div className={s.lbl}>Additional package</div>
              <div className={s.name}>
                {data.additionalPackage.title}
                <button className={s.change} onClick={(e) => setAddonMenu(e.currentTarget)}>
                  Change <ExpandIcon sx={{ fontSize: 15 }} />
                </button>
              </div>
            </div>
            <span className={s.spacer} />
            {data.additionalPackageEndDate ? (
              <span className={`${s.chip} ${s.chipBig} ${addonDays > 0 ? s.chipOk : s.chipBad}`}>
                {addonDays > 0 ? `Renews in ${addonDays} days` : 'Expired'} · {formatDate(data.additionalPackageEndDate, 'dd/MM/yyyy')}
              </span>
            ) : (
              <span className={`${s.chip} ${s.chipBig} ${s.chipMuted}`}>No renewal date</span>
            )}
            <Switch on={irbisProUpgrade} label="IRBIS Pro Advanced" onToggle={toggleUpgrade} busy={busy === 'upgrade'} />
          </div>
          <div className={s.groups}>
            {([['Profiler quotas', 'var(--primary)', PROFILER], ['WebInt quotas', '#7C5CFF', WEBINT]] as const).map(([title, color, items]) => (
              <div key={title} className={s.group}>
                <h4><i style={{ background: color }} />{title}</h4>
                <div className={s.quotas}>
                  {items.map(([key, label]) => (
                    <div key={key} className={`${s.quota} ${quotas[key] === '0' ? s.quotaZero : ''}`}>
                      {editing === key ? (
                        <Editor initial={quotas[key]} onSave={(v) => saveQuota(key, v)} onCancel={() => setEditing(null)} busy={busy === key} />
                      ) : (
                        <>
                          <div className={s.n}>{quotas[key]}</div>
                          <div className={s.t}>{label}</div>
                          {editBtn(key, `Edit ${label.toLowerCase()} quota`)}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={s.empty}>
          <span className={`${s.tier} ${s.tierAddon}`}><AddIcon sx={{ fontSize: 16 }} /></span>
          <div style={{ flex: 1, minWidth: 200 }}>
            <b>No additional package</b>
            <div style={{ fontSize: 12 }}>Profiler and WebInt quotas become available once an add-on is assigned.</div>
          </div>
          <button className={s.btn} onClick={(e) => setAddonMenu(e.currentTarget)}>
            <AddIcon sx={{ fontSize: 16 }} />
            Assign add-on
          </button>
        </div>
      )}

      {/* the package menus in the v2 design — the same options and actions as before */}
      <Menu anchorEl={mainMenu} open={!!mainMenu} onClose={() => setMainMenu(null)} {...menuLook}>
        <MenuItem disabled className={s.menuHead}>Change the main package</MenuItem>
        {mainOptions.length === 0 && <MenuItem disabled className={s.menuItem}>No packages available</MenuItem>}
        {mainOptions.map((pkg: any) =>
          pkg.isAnnual ? (
            <MenuItem key={pkg.id} className={s.menuItem} onClick={(e) => setAnnualMenu({ anchor: e.currentTarget, pkg })}>
              <span className={s.menuTitle}>{pkg.title}</span>
              <span className={s.menuMeta}>monthly · annual</span>
              <ChevronRightRounded sx={{ fontSize: 17 }} className={s.menuArrow} />
            </MenuItem>
          ) : (
            <MenuItem key={pkg.id} className={s.menuItem} onClick={() => selectMain(pkg, false)}>
              <span className={s.menuTitle}>{pkg.title}</span>
              {typeof pkg.price === 'number' && pkg.price > 0 && <span className={s.menuMeta}>€{pkg.price}</span>}
              {pkg.type && pkg.type !== 'general' && <span className={s.menuTag}>{pkg.type}</span>}
            </MenuItem>
          ),
        )}
      </Menu>
      <Menu
        anchorEl={annualMenu?.anchor}
        open={!!annualMenu}
        onClose={() => setAnnualMenu(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        {...menuLook}
      >
        {annualMenu && [
          <MenuItem key="h" disabled className={s.menuHead}>{annualMenu.pkg.title}</MenuItem>,
          <MenuItem key="m" className={s.menuItem} onClick={() => selectMain(annualMenu.pkg, false)}>
            <span className={s.menuTitle}>Monthly</span>
            <span className={s.menuMeta}>{typeof annualMenu.pkg.price === 'number' ? `€${annualMenu.pkg.price}` : ''} · {annualMenu.pkg.productId}</span>
          </MenuItem>,
          <MenuItem key="a" className={s.menuItem} onClick={() => selectMain(annualMenu.pkg, true)}>
            <span className={s.menuTitle}>Annual</span>
            <span className={s.menuMeta}>{typeof annualMenu.pkg.annualPrice === 'number' ? `€${annualMenu.pkg.annualPrice}` : ''} · {annualMenu.pkg.annualProductId}</span>
          </MenuItem>,
        ]}
      </Menu>
      <Menu anchorEl={addonMenu} open={!!addonMenu} onClose={() => setAddonMenu(null)} {...menuLook}>
        <MenuItem disabled className={s.menuHead}>Assign an add-on</MenuItem>
        {addonOptions.length === 0 && <MenuItem disabled className={s.menuItem}>No add-on packages available</MenuItem>}
        {addonOptions.map((pkg: any) => (
          <MenuItem key={pkg.id} className={s.menuItem} onClick={() => selectAddon(pkg)}>
            <span className={s.menuTitle}>{pkg.title}</span>
            {typeof pkg.price === 'number' && pkg.price > 0 && <span className={s.menuMeta}>€{pkg.price}</span>}
          </MenuItem>
        ))}
      </Menu>
    </div>
  )
}
